import React from 'react';
import {View, Text, TouchableOpacity, StyleSheet} from 'react-native';
import {MaterialCommunityIcons} from '@expo/vector-icons';
import I18n from 'i18n-js';
import Divider from './menuDivider';
import drawerStyles from '../../style/drawer';
import {fontFamily} from '../../helpers/fontFamily';
import {
  touchTargetMin,
  teamAccent,
  bandTeamBorder,
  bandTeamRailBg,
  bandTeamChipBg,
  bandTeamChipBorder
} from '../../style/adminTools';

export const TEAM_DESTINATIONS = [
  {
    route: 'AdminTenants',
    translationKey: 'adminTools.teamMenuTenants',
    icon: 'office-building',
    testID: 'teamTenants',
    alsoActiveFor: ['AdminTenantPage']
  },
  {
    route: 'AdminUtilities',
    translationKey: 'adminTools.teamMenuUtilities',
    icon: 'tools',
    testID: 'teamUtilities'
  }
];

const isDestinationActive = ({route, alsoActiveFor = []}, activeRoute) =>
  activeRoute === route || alsoActiveFor.includes(activeRoute);

const iconColor = drawerStyles.iconnewCommonText.color;

export const TeamOnlyChip = () => (
  <View testID="teamOnlyChip" style={styles.chip}>
    <MaterialCommunityIcons name="lock" size={12} style={{color: teamAccent}} />
    <Text style={styles.chipText}>{I18n.t('adminTools.teamOnly')}</Text>
  </View>
);

const TeamDrawerItem = ({testID, label, icon, active, onPress, children}) => (
  <TouchableOpacity
    testID={testID}
    accessibilityRole="button"
    accessibilitySelected={!!active}
    onPress={onPress}
    style={[styles.item, active && styles.itemActive]}>
    <View style={styles.itemIcon}>
      {children || (
        <MaterialCommunityIcons
          name={icon}
          size={24}
          style={{color: active ? teamAccent : iconColor}}
        />
      )}
    </View>
    <Text
      style={[
        drawerStyles.iconnewCommonText,
        styles.itemText,
        active && styles.itemTextActive
      ]}>
      {label}
    </Text>
  </TouchableOpacity>
);

export const TeamDrawerItems = ({
  activeRoute,
  onNavigate,
  onReportBug,
  onLogout
}) => (
  <View style={styles.items}>
    <Divider marginTop={30} style={styles.expandedDivider} />
    {TEAM_DESTINATIONS.map(destination => (
      <TeamDrawerItem
        key={destination.route}
        testID={destination.testID}
        label={I18n.t(destination.translationKey)}
        icon={destination.icon}
        active={isDestinationActive(destination, activeRoute)}
        onPress={() => onNavigate(destination.route)}
      />
    ))}
    <Divider marginTop={20} style={styles.expandedDivider} />
    <TeamDrawerItem
      testID="reportBug"
      label={I18n.t('adminTools.teamMenuReportBug')}
      onPress={onReportBug}>
      <i className="icon-bug" style={drawerStyles.iconnewCommon}></i>
    </TeamDrawerItem>
    <TeamDrawerItem
      testID="logout"
      label={I18n.t('adminTools.teamMenuLogOut')}
      onPress={onLogout}>
      <i
        className="icon-logout"
        style={{
          ...drawerStyles.iconnewCommon,
          transform: 'rotate(180deg)'
        }}></i>
    </TeamDrawerItem>
  </View>
);

const TeamCollapsedItem = ({testID, label, active, onPress, children}) => (
  <TouchableOpacity
    testID={testID}
    accessibilityRole="button"
    accessibilityLabel={label}
    accessibilitySelected={!!active}
    onPress={onPress}
    style={[styles.collapsedItem, active && styles.itemActive]}>
    {children}
  </TouchableOpacity>
);

export const TeamCollapsedItems = ({
  initial,
  activeRoute,
  onNavigate,
  onReportBug,
  onLogout
}) => (
  <View style={styles.collapsedItems}>
    <View
      testID="teamOnlyTile"
      accessibilityLabel={I18n.t('adminTools.teamOnly')}
      style={styles.miniTile}>
      <Text style={styles.miniTileText}>{initial}</Text>
    </View>
    <Divider marginTop={10} />
    {TEAM_DESTINATIONS.map(destination => {
      const active = isDestinationActive(destination, activeRoute);
      return (
        <TeamCollapsedItem
          key={destination.route}
          testID={destination.testID}
          label={I18n.t(destination.translationKey)}
          active={active}
          onPress={() => onNavigate(destination.route)}>
          <MaterialCommunityIcons
            name={destination.icon}
            size={22}
            style={{color: active ? teamAccent : iconColor}}
          />
        </TeamCollapsedItem>
      );
    })}
    <Divider marginTop={20} />
    <TeamCollapsedItem
      testID="reportBug"
      label={I18n.t('adminTools.teamMenuReportBug')}
      onPress={onReportBug}>
      <i className="icon-bug" style={{fontSize: '24px', color: iconColor}}></i>
    </TeamCollapsedItem>
    <TeamCollapsedItem
      testID="logout"
      label={I18n.t('adminTools.teamMenuLogOut')}
      onPress={onLogout}>
      <i
        className="icon-logout"
        style={{
          fontSize: '24px',
          color: iconColor,
          transform: 'rotate(180deg)'
        }}></i>
    </TeamCollapsedItem>
  </View>
);

const styles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    marginTop: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: bandTeamChipBorder,
    backgroundColor: bandTeamChipBg
  },
  chipText: {
    marginLeft: 4,
    fontFamily: fontFamily.font700,
    fontSize: 11,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: teamAccent
  },
  expandedDivider: {
    alignSelf: 'flex-start'
  },
  items: {
    marginBottom: '3rem'
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: touchTargetMin,
    marginTop: 12,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'transparent'
  },
  itemActive: {
    backgroundColor: bandTeamRailBg,
    borderColor: bandTeamBorder
  },
  itemIcon: {
    width: 30,
    height: 30,
    justifyContent: 'center'
  },
  itemText: {
    marginTop: 0
  },
  itemTextActive: {
    color: '#fff'
  },
  collapsedItems: {
    alignItems: 'center',
    marginBottom: '3rem'
  },
  collapsedItem: {
    width: touchTargetMin,
    height: touchTargetMin,
    marginTop: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center'
  },
  miniTile: {
    width: 40,
    height: 40,
    marginTop: 12,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: teamAccent,
    justifyContent: 'center',
    alignItems: 'center'
  },
  miniTileText: {
    fontFamily: fontFamily.font700,
    fontStyle: 'italic',
    fontSize: 26,
    textTransform: 'uppercase',
    color: teamAccent
  }
});
